import { recipes } from '../data/recipes';

const defaultGuidance = {
  path: '*',
  pageLabel: 'General Page',
  primaryKeyword: 'gourmet dessert mixes',
  titleTagExample: 'Mirage Fine Desserts | Premium Dessert and Bakery Mixes',
  metaDescriptionExample:
    'Explore premium dessert and bakery mixes designed for reliable results, faster production, and stronger menu performance.',
  schemaType: 'WebPage',
  internalLinks: ['/pastry-mixes', '/bakery-mixes', '/contact-us'],
  contentAngle: 'Show commercial kitchen outcomes: consistency, speed, and profitability.'
};

const routeGuidance = [
  {
    path: '/',
    pageLabel: 'Home',
    primaryKeyword: 'dessert mix manufacturer',
    titleTagExample: 'Mirage Fine Desserts | Dessert Mix Manufacturer for Foodservice',
    metaDescriptionExample:
      'Mirage Fine Desserts manufactures premium pastry and bakery mixes for foodservice teams that need speed, consistency, and quality.',
    schemaType: 'Organization + WebSite',
    internalLinks: ['/pastry-mixes', '/bakery-mixes', '/the-mirage-difference'],
    contentAngle: 'Lead with value proposition and trust signals in the first viewport.'
  },
  {
    path: '/pastry-mixes',
    pageLabel: 'Pastry Mixes',
    primaryKeyword: 'pastry mix base',
    titleTagExample: 'Pastry Mix Bases | Creme Brulee, Mousse, Panna Cotta and More',
    metaDescriptionExample:
      'Discover Mirage pastry mix bases for creme brulee, mousse, panna cotta, and gelato with faster prep and consistent results.',
    schemaType: 'CollectionPage + Product',
    internalLinks: ['/mirage-recipe', '/contact-us', '/the-mirage-difference'],
    contentAngle: 'Add FAQ blocks for preparation, storage, and kitchen throughput use cases.'
  },
  {
    path: '/bakery-mixes',
    pageLabel: 'Bakery Mixes',
    primaryKeyword: 'commercial bakery mix',
    titleTagExample: 'Commercial Bakery Mixes | Cakes, Brownies, Churros, Waffles',
    metaDescriptionExample:
      'Commercial bakery mixes for high-volume operations: lava cake, brownies, churros, crepes, and specialty cakes.',
    schemaType: 'CollectionPage + Product',
    internalLinks: ['/contact-us', '/superior-products', '/q-a'],
    contentAngle: 'Use outcome-driven copy around labor savings and batch consistency.'
  },
  {
    path: '/mirage-recipe',
    pageLabel: 'Recipes Hub',
    primaryKeyword: 'dessert mix recipes',
    titleTagExample: 'Dessert Mix Recipes | Mirage Fine Desserts',
    metaDescriptionExample:
      'Browse Mirage dessert mix recipes with preparation instructions, ingredient ratios, and serving guidance for commercial kitchens.',
    schemaType: 'ItemList + Recipe',
    internalLinks: ['/pastry-mixes', '/contact-us'],
    contentAngle: 'Add recipe schema and jump links to improve SERP visibility and engagement.'
  },
  {
    path: '/contact-us',
    pageLabel: 'Contact',
    primaryKeyword: 'dessert mix supplier contact',
    titleTagExample: 'Contact Mirage Fine Desserts | Commercial Mix Supplier',
    metaDescriptionExample:
      'Contact Mirage Fine Desserts to discuss pastry and bakery mix supply, sampling, and production planning for your operation.',
    schemaType: 'ContactPage + Organization',
    internalLinks: ['/pastry-mixes', '/bakery-mixes'],
    contentAngle: 'Ensure form CTA and value proposition are above the fold for conversion.'
  }
];

const recipeDetailGuidance = {
  path: '/recipe-detail',
  pageLabel: 'Recipe Detail',
  primaryKeyword: 'commercial dessert recipe',
  titleTagExample: 'Commercial Dessert Recipe | [Recipe Name] - Mirage Fine Desserts',
  metaDescriptionExample:
    'Get commercial dessert recipe instructions using Mirage mixes, including prep time, serving adjustments, and variation ideas.',
  schemaType: 'Recipe',
  internalLinks: ['/mirage-recipe', '/pastry-mixes', '/contact-us'],
  contentAngle: 'Use structured data with prep time and serving metadata on each recipe page.'
};

const recipePaths = new Set(recipes.map((recipe) => recipe.path));

export const seoRouteGuidance = [...routeGuidance, recipeDetailGuidance, defaultGuidance];

export const seoImplementationPhases = [
  {
    phase: 'Phase 1: Technical Foundation (Weeks 1-2)',
    actions: [
      'Implement unique title and meta description generation per route.',
      'Add canonical URLs and Open Graph tags for primary pages.',
      'Generate XML sitemap and submit to search engines.',
      'Validate robots directives and noindex strategy for private routes.'
    ]
  },
  {
    phase: 'Phase 2: Content Optimization (Weeks 3-6)',
    actions: [
      'Refine on-page headings around primary commercial-intent keywords.',
      'Add FAQ and use-case sections for pastry and bakery pages.',
      'Expand recipe intros with search-intent language and internal links.',
      'Standardize product copy to include prep speed and consistency outcomes.'
    ]
  },
  {
    phase: 'Phase 3: Authority + Measurement (Weeks 7-12)',
    actions: [
      'Publish case examples tied to production efficiency gains.',
      'Build partner/backlink opportunities with distributors and suppliers.',
      'Track ranking movement and CTR for targeted route clusters.',
      'Iterate titles and descriptions based on query performance.'
    ]
  }
];

export const seoKpis = [
  'Non-branded organic sessions by route cluster',
  'Top-20 keyword coverage for pastry, bakery, and recipe terms',
  'Click-through rate from search results (title + meta effectiveness)',
  'Contact conversion rate from organic landing pages'
];

export const findSeoGuidanceForPath = (sitePath) => {
  const exact = routeGuidance.find((guidance) => guidance.path === sitePath);

  if (exact) {
    return exact;
  }

  if (recipePaths.has(sitePath)) {
    return recipeDetailGuidance;
  }

  return defaultGuidance;
};
